import { doc, updateDoc, type FieldValue } from "firebase/firestore";
import { useCallback, useState } from "react";

import { db } from "integrations/firebase/firestore";

/**
 * Direct event-settings writes.
 *
 * These are the fields firestore.rules lets an event admin change without a
 * command, because they need no sanitizing, no uniqueness reservation and no
 * fan-out. The rule pins the affected keys to exactly this set, and separately
 * re-validates the WHOLE document afterwards — so sending a valid-looking
 * partial update can still be refused if it would leave the event invalid
 * (an empty title, an endDate before startDate).
 *
 * Description and slug are deliberately absent: both are command-owned.
 *
 * The registration secret is here because it needs no fan-out either; the
 * rules pin it to 6–128 characters whenever the gate is switched on, and
 * the Settings card pre-checks that so the refusal can name the field.
 */
export const EDITABLE_EVENT_FIELDS = [
  "title",
  "location",
  "startDate",
  "endDate",
  "timezone",
  "image",
  "imageThumb",
  "imageMedium",
  "imageFocalX",
  "imageFocalY",
  "configAgenda",
  "configSpeakers",
  "configAttendees",
  "configPartners",
  "configVenues",
  "configResources",
  "configInformation",
  "configNow",
  // TNG-96. Switched on from Settings together with the id of the
  // notification it booked, so a second switch-on can find it.
  "configAfterEvent",
  "afterEventNotificationCommandId",
  "configConnections",
  "registrationSecretEnabled",
  "registrationSecret",
  "published",
  // TNG-92: switching it off cancels every open request (a backend trigger).
  "trackSwitchEnabled",
  // The bold landing page. The rules bound each one; the Settings card and the
  // Agenda tab pre-check the same limits so a refusal can name the field.
  "heroKicker",
  "heroHeadline",
  "heroSubline",
  "heroStats",
  "heroVideo",
  "dayTitles",
] as const;

export type EditableEventField = (typeof EDITABLE_EVENT_FIELDS)[number];

/** A map field: `heroStats` (slot to tile) or `dayTitles` (day to title). */
export type EventMapValue = Record<string, string | Record<string, string>>;

/**
 * `FieldValue` is in the union for `deleteField()`: clearing the hero image
 * has to remove the keys rather than blank them, because the rules type
 * `image` as an optional string and the projection reads an absent key as
 * "no image" while an empty one is a broken `img src`.
 */
export type EventSettingsWrite = Partial<
  Record<
    EditableEventField,
    string | number | boolean | FieldValue | EventMapValue
  >
>;

export function useEventSettingsWrite(eventId: string) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useCallback(
    async (changes: EventSettingsWrite): Promise<boolean> => {
      setSaving(true);
      setError(null);
      try {
        await updateDoc(doc(db, "events", eventId), changes);
        return true;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to save event settings",
        );
        return false;
      } finally {
        setSaving(false);
      }
    },
    [eventId],
  );

  return { save, saving, error, clearError: () => setError(null) };
}
